/**
 * Forge design tokens (SYS-1).
 *
 * These are CSS-variable-backed so the SAME token is theme-aware: every value
 * resolves through the `--c-*` variables defined in styles/global.css, which
 * flip between the light (default) and dark palettes. Rewriting the VALUES here
 * (not the names) makes all ~47 importing files theme-correct without editing
 * them. `rgb(var(--c-x))` works anywhere a CSS color string is accepted —
 * Mantine `c=`/`color=` props, inline `style`, and SVG `color` attributes.
 *
 * IMPORTANT: because these are now `rgb(var(--c-x))` strings, the old
 * hex-concat alpha pattern `${colors.neon}1a` no longer works — use `alpha()`.
 */

export const colors = {
  // Base surfaces
  void: 'rgb(var(--c-void))',
  deep: 'rgb(var(--c-deep))',
  panel: 'rgb(var(--c-panel))',
  card: 'rgb(var(--c-card))',

  // Accents
  neon: 'rgb(var(--c-neon))',
  hudCyan: 'rgb(var(--c-cyan))',
  hudAmber: 'rgb(var(--c-amber))',
  hudRed: 'rgb(var(--c-red))',
  hudMagenta: 'rgb(var(--c-magenta))',
} as const;

/**
 * Accent INKS — the readable foreground of an accent placed on its own 10-12%
 * alpha wash (Badge, Pill, `Button variant="secondary"`).
 *
 * The bright brand accents are tuned to be *surfaces*, not text: on the light
 * card they land around 3.2-3.9:1, under WCAG AA. `--c-*-ink` resolves to a
 * darkened variant in light mode and aliases straight back to the bright accent
 * in dark mode, so one token name is correct in both themes.
 */
export const inks = {
  neon: 'rgb(var(--c-neon-ink))',
  hudCyan: 'rgb(var(--c-cyan-ink))',
  hudAmber: 'rgb(var(--c-amber-ink))',
  hudRed: 'rgb(var(--c-red-ink))',
  hudMagenta: 'rgb(var(--c-magenta-ink))',
} as const;

export type ColorToken = keyof typeof colors;

/** Maps a token to its raw CSS variable (for alpha compositing). */
const COLOR_VARS: Record<ColorToken, string> = {
  void: '--c-void',
  deep: '--c-deep',
  panel: '--c-panel',
  card: '--c-card',
  neon: '--c-neon',
  hudCyan: '--c-cyan',
  hudAmber: '--c-amber',
  hudRed: '--c-red',
  hudMagenta: '--c-magenta',
};

/**
 * Alpha-composited token color, theme-aware.
 * `alpha('neon', 0.1)` → `'rgb(var(--c-neon) / 0.1)'`. Replaces the old
 * `${colors.neon}1a` hex-concat, which produced invalid CSS once tokens became
 * CSS-var-backed.
 */
export function alpha(token: ColorToken, a: number): string {
  return `rgb(var(${COLOR_VARS[token]}) / ${a})`;
}

export const fonts = {
  display: "'Plus Jakarta Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
  mono: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
  body: "'Plus Jakarta Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
} as const;

/**
 * Type scale (px).
 *
 * SYS: `xs` was 9px, which is what `size="xs"` resolved to on ~330 call sites
 * (every card description, table sub-label and KPI eyebrow) — unreadable and the
 * loudest "not a production SaaS" signal in the app. The scale is now monotonic
 * and `xl` is a real step above `lg` instead of jumping 14 → 30; the 30px
 * display size lives on `h1` (pinned explicitly in mantine.ts) and on the
 * `.readout__value` class, which set their own px.
 */
export const fontSizes = {
  xs: 11,
  xxs: 10,
  sm: 12.5,
  md: 14,
  lg: 16,
  xl: 20,
} as const;

/** Display size (px) reserved for h1 / hero readouts. */
export const displaySize = 30;

export const radii = {
  xs: 2,
  sm: 4,
  md: 6,
  lg: 10,
  xl: 14,
} as const;

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
} as const;

// Semantic aliases that reference the palette above.
export const semantic = {
  background: colors.void,
  surface: colors.deep,
  surfaceElevated: colors.panel,
  surfaceCard: colors.card,
  accent: colors.neon,
  info: colors.hudCyan,
  warning: colors.hudAmber,
  danger: colors.hudRed,
  highlight: colors.hudMagenta,
} as const;

// Neutral surface accents — used as borders + chart gridlines.
// These are part of the locked palette (matches __root.tsx + every UI primitive)
// but were previously inlined as raw hex; expose them here so chart and
// recharts components can compose from tokens rather than hand-typed colors.
export const neutral = {
  /** Panel border — used by Card, Modal, Header, Table dividers, KPI tiles. */
  border: 'var(--c-bd)',
  /** Elevated border — used by `Card variant="elevated"`. */
  borderStrong: 'var(--c-bd-bright)',
  /** Body / `Text c="dimmed"` foreground. */
  textDim: 'rgb(var(--c-txt-2))',
  /** Inactive icon tint. */
  iconMuted: 'rgb(var(--c-txt-muted))',
  /** Subtle muted ink for sort indicators. */
  inkMuted: 'rgb(var(--c-txt-muted))',
} as const;
